import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaCardContainer, AspectRatio } from "./MediaCardContainer";
import { MediaDisplay } from "./MediaDisplay";
import * as Overlay from "./MediaOverlay";

export type MediaCardViewMode = "grid" | "list" | "selector" | "minimal";

export interface MediaCardProps {
  mediaItem: MediaItem;
  viewMode?: MediaCardViewMode;
  aspectRatio?: AspectRatio;
  useThumbnail?: boolean;
  showPlayButton?: boolean;

  // Selection
  isSelected?: boolean;
  selectionIndex?: number;
  showNumbering?: boolean;
  onSelect?: (id: number, event: React.MouseEvent) => void;

  // Interaction
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;

  // Composable overlays
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  bottomRight?: React.ReactNode;

  // Layout modifiers
  className?: string;
}

/**
 * Unified MediaCard component
 *
 * Replaces: AdminMediaCard, ApprovalMediaCard, EditorMediaCard, PostSelectorMediaCard, PostMediaCard
 *
 * Usage patterns:
 *
 * 1. Admin with full controls:
 *    <MediaCard
 *      mediaItem={item}
 *      topLeft={<StageDropdown />}
 *      topRight={<ContextMenu />}
 *      bottomLeft={<LabelBadge />}
 *      bottomRight={<AiBadge />}
 *    />
 *
 * 2. Selector mode:
 *    <MediaCard
 *      mediaItem={item}
 *      viewMode="selector"
 *      isSelected={selected}
 *      onSelect={handleSelect}
 *    />
 *
 * 3. Minimal (in posts):
 *    <MediaCard
 *      mediaItem={item}
 *      viewMode="minimal"
 *      aspectRatio="1:1"
 *    />
 */
export const MediaCard: React.FC<MediaCardProps> = ({
  mediaItem,
  viewMode = "grid",
  aspectRatio: propAspectRatio,
  useThumbnail = false,
  showPlayButton = true,
  isSelected = false,
  selectionIndex = 0,
  showNumbering = false,
  onSelect,
  onClick,
  disabled = false,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  className,
}) => {
  // Videos should always display at 9:16 aspect ratio (unless overridden)
  const effectiveAspectRatio = propAspectRatio || (mediaItem.isVideo() ? "9:16" : "4:5");

  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Don't trigger if clicking interactive elements
    if (target.closest("[data-interactive]")) {
      return;
    }

    // Handle selection mode
    if (onSelect) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(mediaItem.id, e);
      return;
    }

    // Handle custom click
    onClick?.(e);
  };

  const isMinimal = viewMode === "minimal";
  const isSelector = viewMode === "selector";

  return (
    <MediaCardContainer
      aspectRatio={effectiveAspectRatio}
      selected={isSelected}
      disabled={disabled || mediaItem.isTaken}
      onClick={handleCardClick}
      className={className}
    >
      <MediaDisplay
        mediaItem={mediaItem}
        useThumbnail={useThumbnail}
        showPlayButton={showPlayButton && !isMinimal}
      />

      {/* Top-left overlay */}
      {!isMinimal && topLeft && (
        <Overlay.TopLeft showOnHover={!isSelector && !isSelected} interactive>
          {topLeft}
        </Overlay.TopLeft>
      )}

      {/* Top-right overlay */}
      {!isMinimal && topRight && (
        <Overlay.TopRight showOnHover>
          {topRight}
        </Overlay.TopRight>
      )}

      {/* Bottom-left overlay */}
      {!isMinimal && bottomLeft && (
        <Overlay.BottomLeft>
          {bottomLeft}
        </Overlay.BottomLeft>
      )}

      {/* Bottom-right overlay */}
      {!isMinimal && bottomRight && (
        <Overlay.BottomRight>
          {bottomRight}
        </Overlay.BottomRight>
      )}
    </MediaCardContainer>
  );
};
